/**
 * THE AURA — the god's area of focus, made visible, and the thing the world's
 * detail budget is spent inside.
 *
 * Two ideas that were separate until now, joined because they are the same
 * circle:
 *
 *  · **Where a god's attention IS.** `ui/presence.ts` has always had a
 *    distance and `sim/simulation.ts` has always had a focus radius; neither
 *    was ever drawn, so the scale of attention was a number in the HUD rather
 *    than a place in the world. Law 12 — *the world is the interface* — says
 *    it should be a place.
 *  · **Where the world is drawn finely.** `world/chunkManager.ts`'s LOD 0
 *    ring is 768 m and costs, measured on the shipped island, 6,374 tree
 *    instances across 700 instanced draws. That ring is the single largest
 *    line in the frame's bill, and until now its radius was a settings slider
 *    nobody sees.
 *
 * Making them one circle is what pays for everything else. Near meshes are
 * `chunks × kinds × variants` and the chunk count goes with **radius²**, so
 * tightening the ring buys variety linearly: at 512 m the near ring holds
 * ~2,850 tree instances in ~310 meshes, which frees roughly 390 draws — a
 * 2.25× variant pool for the draw calls the game already spends.
 *
 * ## What this is NOT, yet
 *
 * Not a skill-tree purchase, not an Anchor, and not visible to mortals.
 *
 * The last of those is law 10 and it is not negotiable: *to every eye in the
 * world a divine act is simply unexplained.* An aura a mortal could see is a
 * god wearing a nameplate. This draws for the player and, later, for other
 * gods — peers — and for nobody else. Nothing in the simulation reads it.
 *
 * Law 31 already names an Aura carried by an Anchor: *manual compresses
 * causality; aura accumulates it.* That is the aura a god LEAVES. This is the
 * one a god CARRIES, and keeping them distinct is deliberate.
 */
import * as THREE from 'three';

export interface Aura {
  group: THREE.Group;
  /** Metres. The radius of attention, and of the fine-detail ring. */
  radius: number;
  /** Draw the ring and the light at all. */
  visible: boolean;
  /**
   * Put it where the god is and let it breathe. `t` is seconds; the pulse is
   * slow and small on purpose — a divine thing is unnaturally precise, not
   * animated.
   *
   * `groundAt` drapes the ring on the land. Without it the ring is flat at
   * the height of the ground under the EYE, which on any real slope leaves it
   * hanging in the air over a hillside — measured at the camp station, where
   * a 380 m ring floated clean across a rise. A god's circle of attention is
   * a thing ON the country, so it follows the country.
   */
  update(x: number, y: number, z: number, groundY: number, t: number,
         groundAt?: (x: number, z: number) => number): void;
  /** The LOD multiplier this radius implies. See `detailFor`. */
  detailScale(): number;
  dispose(): void;
}

/**
 * The divine palette (`main.ts`'s `DIVINE_GLOW`): warm ivory, subtly
 * luminous, unnaturally precise. One copy of the number, and it is the one
 * the presence's own point light already uses.
 */
const GLOW = 0xfbeedb;

/** Where the game's own LOD 0 ring sits today, in metres — `chunkManager`'s
 *  `dist <= 3` chunks of 256 m. The multiplier is relative to this. */
const BASE_LOD0 = 3 * 256;

/**
 * THE CEILING, and it is the STREAMER's, not the frame's.
 *
 * Swept at `?cap=trees` (a five-metre eye, where near detail costs most),
 * min-of-24 synchronous renders each followed by `gl.finish()` — RAF
 * wall-clock is useless in the preview pane, which rate-limits and reported a
 * flat 40.1 ms for every radius from 384 to 1280:
 *
 *   | detail | ring     | render min | calls | triangles |
 *   |--------|----------|------------|-------|-----------|
 *   | 0.5    |   384 m  |   2.7 ms   |   759 |   6.38 M  |
 *   | 1.0    |   768 m  |   4.6 ms   | 1,190 |   8.78 M  |
 *   | 1.6    | 1,229 m  |   6.3 ms   | 1,264 |   9.17 M  |
 *   | 2.2    | 1,690 m  |   6.1 ms   | 1,264 |   9.17 M  |
 *   | 3.0    | 2,304 m  |   6.1 ms   | 1,264 |   9.17 M  |
 *
 * **There is no cliff.** 2.7 ms to 6.3 ms across the whole usable span, well
 * inside a 16.7 ms frame. What there IS is saturation: identical calls and
 * triangles at 1.6, 2.2 and 3.0, because `world/chunkManager.ts` streams a
 * finite ring and the LOD ladder cannot promote chunks that are not loaded.
 *
 * So the clamp below is not a performance guard — it is the truth. Past it
 * the ring would draw wider while delivering nothing, and an aura that
 * promises detail it is not giving is worse than no aura.
 *
 * To go further, widen the streamer first. The frame has the room.
 */
export const MAX_EFFECTIVE = 1229;

/**
 * The radius the aura opens at — the shipped LOD-0 ring, exactly.
 *
 * It was 512 while the budget looked tight; the sweep above says it is not,
 * so the default costs nothing anybody was not already paying and the aura
 * becomes a visible statement of a ring the game has always had. Reach can
 * grow it later; the settings panel's own multiplier still pulls it in.
 */
export const DEFAULT_AURA = 768;

/**
 * `detailScale` for a given aura radius.
 *
 * `chunkManager.lodFor` reads `dist <= 3 * s` in CHUNKS, so the multiplier is
 * simply the aura's radius over the ring it replaces. Clamped because the
 * streamer's own ring has to stay outside the detail ring — a detail rung
 * wider than the loaded ring would ask for chunks that are not there.
 */
export const detailFor = (radius: number): number =>
  Math.max(0.35, Math.min(MAX_EFFECTIVE / BASE_LOD0, radius / BASE_LOD0));

export function createAura(radius = DEFAULT_AURA): Aura {
  const group = new THREE.Group();
  group.name = 'aura';
  group.renderOrder = 3;

  /*
   * THE RING, drawn on the ground rather than as a sphere.
   *
   * A sphere is what the aura IS — a volume of attention around the eye — and
   * it is the wrong thing to draw. Seen from inside, a translucent shell puts
   * a film over the whole frame, which is exactly the veil that must not come
   * between a god and the country it is reading. The ground ring says the
   * same thing and says it where the player is already looking: at the land.
   *
   * Additive and unlit, so it reads as light rather than as a painted line,
   * and `depthWrite` off so it never occludes what it is drawn over.
   */
  /*
   * Built by hand rather than from `RingGeometry`, because it has to be
   * DRAPED: a strip of `SEG` quads whose vertices carry their own world
   * position, re-seated on the terrain whenever the god has moved far enough
   * to matter. A ring at one height is a hoop hanging over the first hill it
   * meets.
   */
  const SEG = 160;
  const ringGeo = new THREE.BufferGeometry();
  {
    const pos = new Float32Array((SEG + 1) * 2 * 3);
    const idx: number[] = [];
    for (let i = 0; i <= SEG; i++) {
      const a = (i / SEG) * Math.PI * 2;
      const ci = Math.cos(a), si = Math.sin(a);
      // inner and outer rim, in UNIT radius — `drape` scales and seats them
      pos[i * 6] = ci * 0.986; pos[i * 6 + 2] = si * 0.986;
      pos[i * 6 + 3] = ci; pos[i * 6 + 5] = si;
      if (i < SEG) {
        const b = i * 2;
        idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
      }
    }
    ringGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    ringGeo.setIndex(idx);
    ringGeo.computeBoundingSphere();
  }
  const ringMat = new THREE.MeshBasicMaterial({
    color: GLOW, transparent: true, opacity: 0.34, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
  });
  const ring = new THREE.Mesh(ringGeo, ringMat);
  ring.name = 'aura-ring';
  ring.frustumCulled = false;
  group.add(ring);

  /*
   * …and a soft inner wash, so the ring reads as the EDGE of something rather
   * than as a hoop lying on the ground. One more ring, wide and very faint,
   * just inside the boundary.
   */
  const washGeo = new THREE.RingGeometry(0.90, 0.999, 96).rotateX(-Math.PI / 2);
  const washMat = new THREE.MeshBasicMaterial({
    color: GLOW, transparent: true, opacity: 0.055, depthWrite: false,
    blending: THREE.AdditiveBlending, side: THREE.DoubleSide, fog: true,
  });
  const wash = new THREE.Mesh(washGeo, washMat);
  wash.name = 'aura-wash';
  wash.frustumCulled = false;
  group.add(wash);

  /**
   * THE LIGHT — small, at the god's own position.
   *
   * Deliberately not a second sun. `main.ts` already carries a point light at
   * the eye for the night pool; this is a smaller, closer one whose whole job
   * is to say *something is here* on the ground beneath the presence, so the
   * aura has a centre as well as an edge. Its range is a fraction of the
   * radius: a god's wide attention is not a lamp (`main.ts`, on the presence
   * glow), and a light that filled the aura would flatten everything in it.
   */
  const light = new THREE.PointLight(GLOW, 0, 0, 2);
  light.name = 'aura-light';
  group.add(light);

  /** Where the ring was last seated, so it is not re-draped every frame. */
  const drapedAt = { x: Infinity, z: Infinity, r: 0 };

  const aura: Aura = {
    group,
    radius,
    visible: true,
    update(x, y, z, groundY, t, groundAt) {
      group.visible = aura.visible;
      if (!aura.visible) return;
      // …and never wider than the detail actually reaches. See `MAX_EFFECTIVE`:
      // beyond it the streamer has no chunks to promote, so a bigger ring
      // would be drawing a promise the world is not keeping.
      const r = Math.max(1, Math.min(aura.radius, MAX_EFFECTIVE));
      // a slow, small breath — 1.5% over eleven seconds. Enough that the edge
      // is alive, far too little to read as an animation.
      const breathe = 1 + Math.sin(t * 0.57) * 0.015;
      const rr = r * breathe;

      /*
       * Re-drape when the god has moved a twentieth of the radius, or the
       * radius itself changed. Every frame would be 320 heightfield samples
       * for a ring that has not moved; a twentieth is under a pixel of slide
       * at any altitude the ring is legible from.
       */
      const moved = Math.hypot(x - drapedAt.x, z - drapedAt.z) > r * 0.05
                 || Math.abs(rr - drapedAt.r) > r * 0.02;
      if (groundAt && moved) {
        drapedAt.x = x; drapedAt.z = z; drapedAt.r = rr;
        const pos = ringGeo.attributes.position as THREE.BufferAttribute;
        const arr = pos.array as Float32Array;
        for (let i = 0; i <= SEG; i++) {
          const a = (i / SEG) * Math.PI * 2;
          const ci = Math.cos(a), si = Math.sin(a);
          const gx = x + ci * rr, gz = z + si * rr;
          // …and never below the sea, which is where the ring would otherwise
          // vanish under water on a coastal aura
          const gy = Math.max(groundAt(gx, gz), 0) + 0.45;
          arr[i * 6] = ci * rr * 0.986; arr[i * 6 + 1] = gy; arr[i * 6 + 2] = si * rr * 0.986;
          arr[i * 6 + 3] = ci * rr; arr[i * 6 + 4] = gy; arr[i * 6 + 5] = si * rr;
        }
        pos.needsUpdate = true;
        ringGeo.computeBoundingSphere();
      }
      // the strip carries its own world offsets, so the mesh only translates
      ring.position.set(x, 0, z);
      ring.scale.set(1, 1, 1);
      wash.position.set(x, groundY + 0.3, z);
      wash.scale.set(rr, 1, rr);
      // the light rides just above the ground under the eye, not at the eye:
      // it is a mark on the land, and a light at head height lights the air
      light.position.set(x, groundY + Math.min(14, Math.max(2.5, (y - groundY) * 0.12)), z);
      light.distance = r * 0.16;
      // …and it dims as the eye climbs. From a kilometre up a pool of light
      // on the ground is a dot; the ring is what carries the aura from there.
      const high = Math.min(1, Math.max(0, (y - groundY) / 900));
      light.intensity = (1 - high) * 90;
    },
    detailScale: () => detailFor(aura.radius),
    dispose() {
      ringGeo.dispose(); ringMat.dispose();
      washGeo.dispose(); washMat.dispose();
      group.clear();
    },
  };
  return aura;
}
